import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Spin } from 'antd';

import './notification-settings.scss';

// Presentational notifications tab for the Settings redesign.
// Rows render an antd Switch on the right; each switch adapts its boolean
// onChange back into the { target: { checked } } shape the existing
// UserProfile handlers read, so wiring/behavior is unchanged.
const NotificationSettings = (props) => {
  const {
    loading,
    notificationFaxEmail,
    emailNotificationEmailedRecords,
    emailNotificationAssignedRecords,
    emailNotificationEmrUploadFailures,
    failedSentFaxesNotification,
    onRecordsReceived,
    onRecordsEmailed,
    onAssigned,
    onEmrUploadFailures,
    onFailedFaxes,
    showFaxFailureAlerts,
    faxFailureAlerts,
    onFaxFailureAlerts,
    teams,
    onTeamNotification,
  } = props;

  // ENG-3340: the immediate alert row exists only for organizations with the
  // failed-fax visibility feature; flag-off renders the group unchanged.
  const faxFailureAlertRows = showFaxFailureAlerts ? [{
    key: 'fax-failure-alert',
    name: 'Failed faxes',
    description: 'Email me as soon as a fax I sent fails, with the reason and a link to resend it.',
    checked: faxFailureAlerts,
    onChange: onFaxFailureAlerts,
  }] : [];

  const groups = [
    {
      title: 'Inbox activity',
      rows: [
        {
          key: 'fax',
          name: 'Records received',
          description: 'Email me when new records arrive by fax.',
          checked: notificationFaxEmail,
          onChange: onRecordsReceived,
        },
        {
          key: 'email',
          name: 'Records received via email',
          description: 'Email me when new records arrive by email.',
          checked: emailNotificationEmailedRecords,
          onChange: onRecordsEmailed,
        },
      ],
    },
    {
      title: 'Assignments',
      rows: [
        {
          key: 'assigned',
          name: 'Records assigned to you',
          description: 'Email me when a record is assigned to me.',
          checked: emailNotificationAssignedRecords,
          onChange: onAssigned,
        },
      ],
    },
    {
      title: 'Failures',
      rows: [
        {
          key: 'emr',
          name: 'EMR upload failures',
          description: 'Email me about records I own, or records owned by my department or organization, that fail to upload to the EMR.',
          checked: emailNotificationEmrUploadFailures,
          onChange: onEmrUploadFailures,
        },
        ...faxFailureAlertRows,
        {
          key: 'failed-fax',
          name: 'Daily failed-fax summary',
          description: 'Email me a daily summary of faxes I personally sent that failed in the previous 24 hours, not department or organization totals.',
          checked: failedSentFaxesNotification,
          onChange: onFailedFaxes,
        },
      ],
    },
  ];

  // Teams group (D5) — only when the caller belongs to teams (flag-gated
  // upstream: UserProfile only loads memberships when the `teams` feature is
  // on, so flag-off leaves this array empty and renders nothing new). Each
  // team's switch is the sole, independent control for that team's tagged-record
  // emails. The row onChange keeps the same { target: { checked } } shape the
  // render mapping sends, then forwards the team slug up to UserProfile.
  if (teams.length > 0) {
    groups.push({
      title: 'Teams',
      rows: teams.map((team) => ({
        key: `team-${team.slug}`,
        name: team.name,
        description: `Email me about activity for the ${team.name} team.`,
        checked: team.receives_notifications,
        onChange: (e) => onTeamNotification(team.slug, e.target.checked),
      })),
    });
  }

  return (
    <Spin spinning={loading} size="large">
      <div className="notification-settings">
        <div className="notification-settings__header">
          <h2 className="notification-settings__title">Notifications</h2>
          <p className="notification-settings__subtitle">
            Choose which email notifications you receive from Medsender.
          </p>
        </div>
        {groups.map((group) => (
          <section className="notification-settings__group" key={group.title}>
            <h3 className="notification-settings__group-title">{group.title}</h3>
            <div className="notification-settings__card">
              {group.rows.map((row) => (
                <div className="notification-settings__row" key={row.key}>
                  {/* htmlFor + Switch id: clicking the text toggles the switch,
                      and screen readers announce the setting name. */}
                  <label className="notification-settings__row-text" htmlFor={`notification-switch-${row.key}`}>
                    <div className="notification-settings__row-name">{row.name}</div>
                    <div className="notification-settings__row-desc">{row.description}</div>
                  </label>
                  <Switch
                    id={`notification-switch-${row.key}`}
                    aria-label={row.name}
                    checked={!!row.checked}
                    onChange={(checked) => row.onChange({ target: { checked } })}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </Spin>
  );
};

NotificationSettings.defaultProps = {
  loading: false,
  notificationFaxEmail: false,
  emailNotificationEmailedRecords: false,
  emailNotificationAssignedRecords: false,
  emailNotificationEmrUploadFailures: false,
  failedSentFaxesNotification: false,
  showFaxFailureAlerts: false,
  faxFailureAlerts: true,
  onFaxFailureAlerts: () => {},
  teams: [],
  onTeamNotification: () => {},
};

NotificationSettings.propTypes = {
  loading: PropTypes.bool,
  notificationFaxEmail: PropTypes.bool,
  emailNotificationEmailedRecords: PropTypes.bool,
  emailNotificationAssignedRecords: PropTypes.bool,
  emailNotificationEmrUploadFailures: PropTypes.bool,
  failedSentFaxesNotification: PropTypes.bool,
  onRecordsReceived: PropTypes.func.isRequired,
  onRecordsEmailed: PropTypes.func.isRequired,
  onAssigned: PropTypes.func.isRequired,
  onEmrUploadFailures: PropTypes.func.isRequired,
  onFailedFaxes: PropTypes.func.isRequired,
  showFaxFailureAlerts: PropTypes.bool,
  faxFailureAlerts: PropTypes.bool,
  onFaxFailureAlerts: PropTypes.func,
  teams: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    receives_notifications: PropTypes.bool,
  })),
  onTeamNotification: PropTypes.func,
};

export default NotificationSettings;
